import * as THREE from 'three';
import { Camera } from '../rendering/Camera';
import { GameObject } from '../objects/GameObject';
import { Config } from '../core/Config';

// LOD level enum
export enum LODLevel {
  HIGH,
  MEDIUM,
  LOW
}

/**
 * Interface that objects must implement to support Level of Detail
 */
export interface LODCapable {
  /**
   * Get the current LOD level
   */
  getLODLevel(): LODLevel;
  
  /**
   * Set the LOD level
   * @param level The LOD level to set
   */
  setLODLevel(level: LODLevel): void;
  
  /**
   * Get the object's position for distance calculation
   */
  getPosition(): THREE.Vector3;
  
  /**
   * Check if the object has LOD enabled
   */
  hasLODEnabled(): boolean;
}

/**
 * LOD System for managing distance-based level of detail
 */
export class LODSystem {
  private static instance: LODSystem | null = null;
  
  private camera: Camera;
  private objects: Map<string, GameObject> = new Map();
  private enabled: boolean = Config.LOD_ENABLED;
  
  // LOD distance thresholds
  private highDetailDistance: number = Config.LOD_HIGH_DISTANCE;
  private mediumDetailDistance: number = Config.LOD_MEDIUM_DISTANCE;
  
  // Squared distance thresholds for more efficient calculations
  private highDetailDistanceSq: number;
  private mediumDetailDistanceSq: number;
  
  // Update management
  private updateCounter: number = 0;
  private updateFrequency: number = 10; // Only update every 10 frames
  private readonly distanceHysteresis: number = 2.0; // Prevent LOD "ping-ponging" at boundaries
  
  // Vector reuse to avoid allocations
  private static readonly tempVec3: THREE.Vector3 = new THREE.Vector3();
  
  private constructor(camera: Camera) {
    this.camera = camera;
    
    // Precalculate squared distances
    this.highDetailDistanceSq = this.highDetailDistance * this.highDetailDistance;
    this.mediumDetailDistanceSq = this.mediumDetailDistance * this.mediumDetailDistance;
    
    console.log('LOD System initialized with thresholds:',
      `HIGH: 0-${this.highDetailDistance}, MEDIUM: ${this.highDetailDistance}-${this.mediumDetailDistance}, LOW: >${this.mediumDetailDistance}`);
  }
  
  /**
   * Get the singleton instance
   */
  public static getInstance(camera?: Camera): LODSystem {
    if (!LODSystem.instance && camera) {
      LODSystem.instance = new LODSystem(camera);
    } else if (!LODSystem.instance && !camera) {
      throw new Error('LOD System not initialized. Provide a camera on first call.');
    }
    
    return LODSystem.instance as LODSystem;
  }
  
  /**
   * Register an object with the LOD system
   */
  public registerObject(id: string, object: GameObject): void {
    if (object.hasLODEnabled()) {
      this.objects.set(id, object);
    }
  }
  
  /**
   * Remove an object from the LOD system
   */
  public unregisterObject(id: string): void {
    this.objects.delete(id);
  }
  
  /**
   * Update LOD levels based on camera distance - more efficiently
   */
  public update(): void {
    if (!this.enabled) return;
    
    // Skip updates based on update frequency to reduce processing load
    if (++this.updateCounter % this.updateFrequency !== 0) {
      return;
    }
    
    const cameraPosition = this.camera.getPosition();
    
    // Update LOD for all registered objects
    this.objects.forEach((object) => {
      // Basic checks
      if (!object.hasLODEnabled()) return;
      
      // Get object position
      const objectPosition = object.getPosition();
      
      // Calculate distance SQUARED (avoiding expensive sqrt operation)
      // Reuse vector to avoid garbage collection
      LODSystem.tempVec3.subVectors(objectPosition, cameraPosition);
      const distanceSq = LODSystem.tempVec3.lengthSq();
      
      // Get current LOD level - we'll only change if we cross a threshold with hysteresis
      const currentLevel = object.getLODLevel();
      let newLodLevel = currentLevel;
      
      // Determine appropriate LOD level with hysteresis to prevent flickering
      // at boundary conditions
      switch (currentLevel) {
        case LODLevel.HIGH:
          // Only drop from HIGH to MEDIUM if we're well past the boundary
          if (distanceSq > this.highDetailDistanceSq * (1 + this.distanceHysteresis)) {
            newLodLevel = LODLevel.MEDIUM;
          }
          break;
          
        case LODLevel.MEDIUM:
          // Move to HIGH if we're well within the high detail range
          if (distanceSq < this.highDetailDistanceSq * (1 - this.distanceHysteresis)) {
            newLodLevel = LODLevel.HIGH;
          } 
          // Move to LOW if we're well past the medium detail range
          else if (distanceSq > this.mediumDetailDistanceSq * (1 + this.distanceHysteresis)) {
            newLodLevel = LODLevel.LOW;
          }
          break;
          
        case LODLevel.LOW:
          // Only move from LOW to MEDIUM if we're well within the medium detail range
          if (distanceSq < this.mediumDetailDistanceSq * (1 - this.distanceHysteresis)) {
            newLodLevel = LODLevel.MEDIUM;
          }
          break;
      }
      
      // Only update if the level changed
      if (newLodLevel !== currentLevel) {
        object.setLODLevel(newLodLevel);
      }
    });
  }
  
  /**
   * Set the distance thresholds for LOD transitions
   */
  public setDistanceThresholds(highDetail: number, mediumDetail: number): void {
    if (highDetail >= mediumDetail) {
      console.error('Invalid LOD thresholds: high detail distance must be less than medium detail distance');
      return;
    }
    
    this.highDetailDistance = highDetail;
    this.mediumDetailDistance = mediumDetail;
    
    // Update the squared values too
    this.highDetailDistanceSq = highDetail * highDetail;
    this.mediumDetailDistanceSq = mediumDetail * mediumDetail;
  }
  
  /**
   * Get the current distance thresholds
   */
  public getDistanceThresholds(): { high: number, medium: number } {
    return {
      high: this.highDetailDistance,
      medium: this.mediumDetailDistance
    };
  }
  
  /**
   * Set the update frequency
   * @param frequency Only update LOD every N frames (higher = better performance but slower LOD transitions)
   */
  public setUpdateFrequency(frequency: number): void {
    this.updateFrequency = Math.max(1, frequency);
  }
  
  /**
   * Enable or disable the LOD system
   */
  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }
  
  /**
   * Check if the LOD system is enabled
   */
  public isEnabled(): boolean {
    return this.enabled;
  }
}
